/**
 * Labstep
 *
 * @module state/reducers/entity/byId;
 * @desc Reducers for any entity
 */

import type { Action } from 'labstep-web/models/action.model';
import { Chemical } from 'labstep-web/models/chemical.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { File } from 'labstep-web/models/file.model';
import { Group } from 'labstep-web/models/group.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Organization } from 'labstep-web/models/organization.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import { Resource } from 'labstep-web/models/resource.model';
import { UserGroup } from 'labstep-web/models/user-group.model';
import { normalizeEntities } from 'labstep-web/services/normalize';
import { parentOf } from 'labstep-web/services/schema/helpers';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { RESET_POSITION } from 'labstep-web/state/actions/position';
import { transferOwnershipActionType } from 'labstep-web/state/actions/transfer-ownership';
import { getOptimisticKey } from 'labstep-web/state/epics/optimistic';
import {
  addChildEntityToParent,
  addChildEntityToParentBasedOnBodyEntityUserRoleRequirement,
} from 'labstep-web/state/reducers/helpers/add';
import {
  previewFieldAdd,
  previewFieldRemove,
} from 'labstep-web/state/reducers/helpers/entities/entity';
import {
  metadataEntityRelationPreviewRemove,
  metadataEntityRelationPreviewUpdate,
} from 'labstep-web/state/reducers/helpers/entities/entityRelation';
import { previewArrayUpdate } from 'labstep-web/state/reducers/helpers/entities/entityUser';
import { getStateWithUpdatedLockedAt } from 'labstep-web/state/reducers/helpers/entities/experimentWorkflow';
import { addMetadataOptionToTemplate } from 'labstep-web/state/reducers/helpers/entities/metadata';
import {
  addManyToMany,
  removeManyToMany,
  setManyToMany,
} from 'labstep-web/state/reducers/helpers/manyToMany';
import {
  removeChild,
  removeChildEntityFromParents,
  removeOneToOne,
} from 'labstep-web/state/reducers/helpers/remove';
import { mergeByIds } from 'labstep-web/state/reducers/helpers/utils';
import type { LabstepReduxState } from 'labstep-web/state/types';
import { uniq } from 'lodash';
import camelCase from 'lodash/camelCase';

export const updateEntityById = <T extends EntityName>(
  state: any,
  action: Action,
  entityName: T,
): any => {
  switch (entityName) {
    case Experiment.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL' &&
        action.meta &&
        action.meta.addToProtocolLibrary &&
        action.meta.body &&
        action.meta.body.experiment_id
      ) {
        return {
          ...state,
          [action.meta.body.experiment_id]: {
            ...state[action.meta.body.experiment_id],
            protocol: action.payload?.result,
          },
        };
      }

      if (
        action.type === 'SUCCESS_UPDATE_EXPERIMENT_WORKFLOW' &&
        'locked_at' in action.meta.body
      ) {
        return getStateWithUpdatedLockedAt(state, action);
      }

      if (
        action.type === 'SUCCESS_UPDATE_PROTOCOL_VALUE' &&
        action.meta.body.deleted_at
      ) {
        return removeChild(
          state,
          action.payload?.result,
          'protocol_values',
        );
      }

      if (
        action.type === 'SUCCESS_UPDATE_METADATA' &&
        action.payload.entities?.experiment
      ) {
        const inboundState: any = {};
        const experiments = Object.values(
          action.payload.entities.experiment,
        );
        experiments.forEach((experiment: any) => {
          inboundState[experiment.id] = {
            ...state[experiment.id],
            ...experiment,
          };
        });
        return {
          ...state,
          ...inboundState,
        };
      }

      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL_VALUE' &&
        action.payload.entities?.experiment &&
        action.payload.entities?.protocol_value
      ) {
        const experiments = action.payload.entities.experiment;
        const protocolValues = Object.values(
          action.payload.entities.protocol_value,
        );

        const inboundState: any = {};
        protocolValues.forEach((protocolValue: any) => {
          const experiment = experiments[protocolValue.experiment];
          if (experiment) {
            // Get existing protocol values from state or from previously processed values in this batch
            const existingProtocolValues =
              inboundState[experiment.id]?.protocol_values ||
              (state[experiment.id]?.protocol_values &&
              Array.isArray(state[experiment.id]?.protocol_values)
                ? state[experiment.id].protocol_values
                : []);

            let updatedProtocolValues = uniq([
              ...existingProtocolValues,
              protocolValue.guid,
            ]);

            if (
              !protocolValue.guid.startsWith('-') &&
              action.meta.optimisticCreateGuid
            ) {
              updatedProtocolValues = updatedProtocolValues.filter(
                (guid: string) =>
                  !action.meta.optimisticCreateGuid.includes(guid),
              );
            }

            inboundState[experiment.id] = {
              ...(state[experiment.id] && {
                ...state[experiment.id],
              }),
              ...experiment,
              protocol_values: updatedProtocolValues,
            };
          }
        });
        return {
          ...state,
          ...inboundState,
        };
      }

      if (
        action.type === 'SUCCESS_CREATE_RESOURCE_ITEM' &&
        action.payload.entities?.experiment &&
        action.payload.entities?.protocol_value
      ) {
        const experiments = action.payload.entities.experiment;
        const protocolValues = Object.values(
          action.payload.entities.protocol_value,
        );

        const inboundState: any = {};
        protocolValues.forEach((protocolValue: any) => {
          const experiment = experiments[protocolValue.experiment];
          if (experiment) {
            const existingProtocolValues =
              inboundState[experiment.id]?.protocol_values ||
              (state[experiment.id]?.protocol_values &&
              Array.isArray(state[experiment.id]?.protocol_values)
                ? state[experiment.id].protocol_values
                : []);

            const updatedProtocolValues = uniq([
              ...existingProtocolValues,
              protocolValue.guid,
            ]);

            inboundState[experiment.id] = {
              ...(state[experiment.id] && {
                ...state[experiment.id],
              }),
              ...experiment,
              protocol_values: updatedProtocolValues,
            };
          }
        });

        return {
          ...state,
          ...inboundState,
        };
      }

      break;
    }

    case Protocol.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL' &&
        action.meta &&
        action.meta.addToProtocolLibrary &&
        action.meta.body &&
        action.meta.body.experiment_id
      ) {
        return {
          ...state,
          [action.meta.body.experiment_id]: {
            ...state[action.meta.body.experiment_id],
            protocol: action.payload.result,
          },
        };
      }

      if (
        action.type === 'SUCCESS_UPDATE_EXPERIMENT_WORKFLOW' &&
        'locked_at' in action.meta.body
      ) {
        return getStateWithUpdatedLockedAt(state, action);
      }

      if (
        action.type === 'SUCCESS_UPDATE_PROTOCOL_VALUE' &&
        action.meta.body.deleted_at
      ) {
        return removeChild(
          state,
          action.payload.result,
          'protocol_values',
        );
      }

      if (
        action.type === 'SUCCESS_UPDATE_METADATA' &&
        action.payload.entities?.protocol
      ) {
        const inboundState: any = {};
        const protocols = Object.values(
          action.payload.entities.protocol,
        );
        protocols.forEach((protocol: any) => {
          inboundState[protocol.id] = {
            ...state[protocol.id],
            ...protocol,
          };
        });
        return {
          ...state,
          ...inboundState,
        };
      }

      break;
    }

    case ExperimentWorkflow.entityName: {
      if (
        action.type === 'SUCCESS_UPDATE_EXPERIMENT' &&
        action.meta.body.deleted_at
      ) {
        return removeChild(
          state,
          action.payload?.result,
          'experiments',
        );
      }

      break;
    }

    case ProtocolValue.entityName: {
      if (action.type === 'RECALC_STOICHIOMETRY_TABLE') {
        const entities = action.payload[entityName];
        const newState = {};
        Object.keys(entities).forEach((id: string) => {
          state[id] = { ...state[id], ...entities[id] };
        });
        return {
          ...state,
          ...newState,
        };
      }
      if (action.type === 'SUCCESS_CREATE_CHEMICAL') {
        const { protocol_value_guid, items } = action.meta.body;
        const guids = items
          ? items.map((item) => item.protocol_value_guid)
          : [protocol_value_guid];
        const result = {};
        guids.forEach((guid) => {
          result[guid] = {
            ...state[guid],
            ...action.payload?.entities.protocol_value[guid],
          };
        });
        return {
          ...state,
          ...result,
        };
      }

      if (
        (action.type === 'SUCCESS_CREATE_RESOURCE_ITEM' ||
          action.type === 'SUCCESS_UPDATE_RESOURCE_ITEM') &&
        action.meta &&
        action.meta.body &&
        'protocol_value_origin_guid' in action.meta.body
      ) {
        const { result, entities } = action.payload;

        if (!action.meta.body.protocol_value_origin_guid) {
          return removeOneToOne(
            state,
            action,
            'resource_item_output',
          );
        }

        const newState = {
          ...state,
          [action.meta.body.protocol_value_origin_guid]: {
            ...state[action.meta.body.protocol_value_origin_guid],
            resource_item_output: result,
          },
        };

        return mergeByIds(newState, entities[entityName], entityName);
      }

      if (
        action.type === 'SUCCESS_CREATE_RESOURCE_ITEM' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;
        const resourceItems = action.payload?.entities.resource_item;

        const incomingState: any = {};

        Object.values(resourceItems)
          .filter((item: any) => item.protocol_value_origin)
          .forEach((item: any) => {
            const protocolValue =
              protocolValues[item.protocol_value_origin];
            const variableTemplate =
              state[protocolValue.variable_template_guid];

            if (!variableTemplate?.is_input) {
              protocolValue.resource_item_output = item.id;
              protocolValue.resource_item_output_id = item.id;
            } else {
              protocolValue.resource_item = item.id;
              protocolValue.resource_item_id = item.id;
            }
            incomingState[protocolValue.guid] = {
              ...state[protocolValue.guid],
              ...protocolValue,
            };
          });

        const newState = {
          ...state,
          ...incomingState,
        };

        return newState;
      }

      if (
        action.type === 'SUCCESS_UPDATE_PROTOCOL_VALUE' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;
        const inboundState: any = {};
        Object.values(protocolValues).forEach((pv: any) => {
          inboundState[pv.guid] = {
            ...(state[pv.guid] && { ...state[pv.guid] }),
            ...pv,
          };
        });

        return {
          ...state,
          ...inboundState,
        };
      }

      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL_VALUE' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;

        return mergeByIds({ ...state }, protocolValues, entityName);
      }

      break;
    }

    case ProtocolCondition.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL_VALUE' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;
        const inboundState: any = {};

        const protocolValuesToProcess = Array.isArray(
          action.payload?.result,
        )
          ? Object.values(protocolValues)
          : [
              action.payload?.entities.protocol_value[
                action.payload?.result
              ],
            ];

        protocolValuesToProcess.forEach((protocolValue: any) => {
          const protocolCondition =
            state[protocolValue.protocol_condition];
          if (protocolCondition) {
            if (protocolValue.variable_template_guid) {
              if (
                !Array.isArray(
                  protocolCondition.protocol_values_variable,
                )
              ) {
                protocolCondition.protocol_values_variable = [];
              }
              protocolCondition.protocol_values_variable.push(
                protocolValue.guid,
              );
            } else {
              if (
                !Array.isArray(
                  protocolCondition.protocol_values_constant,
                )
              ) {
                protocolCondition.protocol_values_constant = [];
              }
              protocolCondition.protocol_values_constant.push(
                protocolValue.guid,
              );
            }

            if (!protocolValue.guid.startsWith('-')) {
              protocolCondition.protocol_values_variable =
                protocolCondition.protocol_values_variable.filter(
                  (guid: string) =>
                    !action.meta.optimisticCreateGuid.includes(guid),
                );
              protocolCondition.protocol_values_constant =
                protocolCondition.protocol_values_constant.filter(
                  (guid: string) =>
                    !action.meta.optimisticCreateGuid.includes(guid),
                );
            }

            protocolCondition.protocol_values = [
              ...(protocolCondition.protocol_values_variable || []),
              ...(protocolCondition.protocol_values_constant || []),
            ];

            inboundState[protocolCondition.guid] = protocolCondition;
          }
        });

        return {
          ...state,
          ...inboundState,
        };
      }

      if (
        action.type === 'SUCCESS_CREATE_RESOURCE_ITEM' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;
        const resourceItems = action.payload?.entities.resource_item;

        const incomingState: any = {};
        Object.values(resourceItems).forEach((item: any) => {
          if (
            item.protocol_value_origin &&
            Object.keys(protocolValues).includes(
              item.protocol_value_origin,
            )
          ) {
            const protocolValue =
              protocolValues[item.protocol_value_origin];
            const protocolCondition =
              state[protocolValue.protocol_condition];
            if (protocolCondition) {
              // Get existing protocol values from previously processed items in this batch or from state
              const existingProtocolValues =
                incomingState[protocolCondition.guid]
                  ?.protocol_values ||
                protocolCondition.protocol_values ||
                [];
              const existingProtocolValuesVariable =
                incomingState[protocolCondition.guid]
                  ?.protocol_values_variable ||
                protocolCondition.protocol_values_variable ||
                [];

              incomingState[protocolCondition.guid] = {
                ...protocolCondition,
                protocol_values: [
                  ...existingProtocolValues,
                  item.protocol_value_origin,
                ],
                protocol_values_variable: [
                  ...existingProtocolValuesVariable,
                  item.protocol_value_origin,
                ],
              };
            }
          }
        });

        const newState = {
          ...state,
          ...incomingState,
        };

        return newState;
      }

      if (
        action.type === 'SUCCESS_CREATE_METADATA' &&
        action.payload?.entities.experiment
      ) {
        const metadata =
          action.payload?.entities.metadata[action.payload?.result];
        const protocolConditionGuid =
          metadata.protocol_condition_guid;

        if (!protocolConditionGuid) {
          return state;
        }

        const protocolCondition = state[protocolConditionGuid];

        protocolCondition.metadatas.push(metadata.id);
        return {
          ...state,
          [protocolConditionGuid]: protocolCondition,
        };
      }
      break;
    }

    case File.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_FILE' &&
        'annotated_file_id' in action.meta
      ) {
        return {
          ...mergeByIds(
            state,
            action.payload?.entities[entityName],
            entityName,
          ),
          [action.meta.annotated_file_id]: {
            ...state[action.meta.annotated_file_id],
            image_annotation: action.payload?.result[0],
          },
        };
      }
      break;
    }

    case Metadata.entityName: {
      if (
        action.meta &&
        action.type === 'SUCCESS_UPDATE_ENTITY_RELATION'
      ) {
        return metadataEntityRelationPreviewRemove(state, action);
      }

      if (
        action.meta &&
        action.type === 'SUCCESS_CREATE_ENTITY_RELATION'
      ) {
        return metadataEntityRelationPreviewUpdate(state, action);
      }

      if (
        action.type === 'SUCCESS_UPDATE_METADATA' &&
        action.meta?.body?.options &&
        (action.meta?.denormalized_payload?.variable_template_guid ||
          action.meta?.denormalized_payload?.template_id)
      ) {
        addMetadataOptionToTemplate(state, action);
      }

      if (
        action.type === 'SUCCESS_CREATE_FILE' &&
        action.payload?.entities &&
        action.payload?.entities.file
      ) {
        if (!action.payload) {
          return state;
        }
        const { file: files } = action.payload.entities;
        const inboundState: any = {};
        Object.values(files).forEach((file: any) => {
          if (file.metadata) {
            const metadata = state[`${file.metadata}`];
            inboundState[metadata.id] = {
              ...metadata,
              files: [...metadata.files, file.id],
            };
          }
        });

        return {
          ...state,
          ...inboundState,
        };
      }

      if (
        action.type === 'SUCCESS_CREATE_PROTOCOL_VALUE' &&
        action.payload?.entities &&
        action.payload?.entities.protocol_value
      ) {
        const protocolValues =
          action.payload?.entities.protocol_value;
        const id = action.payload?.result;
        const protocolValue = protocolValues[id];
        if (protocolValue) {
          return mergeByIds({ ...state }, protocolValues, entityName);
        }
        return state;
      }

      if (action.type === 'SUCCESS_CREATE_METADATA') {
        const metadatas = action.payload?.entities.metadata;
        return mergeByIds({ ...state }, metadatas, entityName);
      }
      break;
    }

    case MetadataThread.entityName: {
      if (
        action.type === 'SUCCESS_UPDATE_METADATA' &&
        action.meta.body.deleted_at
      ) {
        return removeChild(
          state,
          action.payload?.result,
          'metadatas',
        );
      }

      if (
        action.type === 'SUCCESS_CREATE_METADATA' &&
        action.payload.entities.metadata &&
        action.payload.entities.metadata_thread
      ) {
        const inboundState: any = {};
        Object.values(action.payload.entities.metadata).forEach(
          (metadata: any) => {
            if (
              metadata.metadata_thread &&
              action.payload.entities.metadata_thread[
                `${metadata.metadata_thread}`
              ]
            ) {
              const metadataThread =
                action.payload.entities.metadata_thread[
                  `${metadata.metadata_thread}`
                ];
              inboundState[metadataThread.id] = {
                ...state[metadataThread.id],
                metadatas: uniq([
                  ...(inboundState[metadataThread.id]?.metadatas ||
                    []),
                  ...((!inboundState[metadataThread.id] &&
                    state[metadataThread.id]?.metadatas) ||
                    []),
                  metadata.id,
                ]),
              };
            }
          },
        );

        return {
          ...state,
          ...inboundState,
        };
      }
      break;
    }

    case Organization.entityName: {
      if (
        action.type === 'SUCCESS_ORGANIZATION_CREATE_CUSTOMER' &&
        action.meta &&
        action.meta.organization &&
        action.meta.organization.id &&
        action.payload &&
        action.payload?.stripe_customer_id
      ) {
        return {
          ...state,
          [action.meta.organization.id]: {
            ...state[action.meta.organization.id],
            stripe_customer_id: action.payload?.stripe_customer_id,
          },
        };
      }
      break;
    }

    case PurchaseOrder.entityName: {
      if (action.type === 'SUCCESS_UPDATE_ORDER_REQUEST') {
        const { body } = action.meta;
        if (body.purchase_order_id) {
          const purchaseOrderId = body.purchase_order_id;
          const orderRequests = (
            state[purchaseOrderId] as PurchaseOrder
          ).order_requests;
          if (orderRequests) {
            return {
              ...state,
              [purchaseOrderId]: {
                ...state[purchaseOrderId],
                order_requests: [
                  ...orderRequests,
                  action.payload?.result,
                ],
              },
            };
          }
        }
      }
      break;
    }

    case Resource.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_RESOURCE_ITEM' &&
        action.meta &&
        action.meta.body &&
        action.meta.body.is_template
      ) {
        const resourceItemTemplateId = action.payload?.result;
        const resourceId = action.meta.body.resource_id;

        return {
          ...state,
          [resourceId]: {
            ...state[resourceId],
            resource_item_template: resourceItemTemplateId,
          },
        };
      }
      if (
        action.type === 'SUCCESS_CREATE_ORDER_REQUEST' &&
        action.meta.body?.is_template
      ) {
        const orderRequestTemplateId = action.payload?.result;
        const resourceId = action.meta.body.template_resource_id;
        return {
          ...state,
          [resourceId]: {
            ...state[resourceId],
            order_request_template: orderRequestTemplateId,
            order_request_template_id: orderRequestTemplateId,
          },
        };
      }
      break;
    }

    case Chemical.entityName: {
      if (action.type === 'RECALC_STOICHIOMETRY_TABLE') {
        const entities = action.payload[entityName];
        const newState = {};
        Object.keys(entities).forEach((id: string) => {
          state[id] = { ...state[id], ...entities[id] };
        });
        return {
          ...state,
          ...newState,
        };
      }
      break;
    }

    case UserGroup.entityName: {
      if (
        action.type === `SUCCESS_UPDATE_USER_GROUP` &&
        'is_home' in action.meta.body
      ) {
        const existingUserGroupState =
          state[action.meta.identifier] || {};
        return Object.keys(state).reduce(
          (result, key) => {
            if (Number(key) === Number(action.meta.identifier)) {
              return result;
            }
            return {
              ...result,
              [key]: {
                ...state[key],
                is_home: false,
              },
            };
          },
          {
            [action.meta.identifier]: {
              ...existingUserGroupState,
              ...action.payload?.entities.user_group[
                action.meta.identifier
              ],
            },
          },
        );
      }
      break;
    }

    case Group.entityName: {
      if (
        action.type === 'SUCCESS_CREATE_PURCHASE_ORDER' &&
        !!action.meta.body.is_template
      ) {
        const groupId = action.meta.body.group_id;
        return {
          ...state,
          [groupId]: {
            ...state[groupId],
            purchase_order_template_id: action.payload?.result,
          },
        };
      }
      break;
    }

    default:
      break;
  }

  if (action.type === 'SUCCESS_UPDATE_FILE') {
    if (
      entityName === 'metadata' &&
      action.meta.body.metadata_id === null
    ) {
      return Object.keys(state).reduce((result, key) => {
        const metadata = (
          state as LabstepReduxState['entities']['metadata']['byId']
        )[key] as Metadata;
        return {
          ...result,
          [key]: {
            ...state[key],
            files: !metadata?.files
              ? []
              : metadata.files.filter(
                  (fileId) => fileId !== action.meta.params.id,
                ),
          },
        };
      }, {});
    }
  }

  return null;
};

/**
 * Returns default reducer for entities organized by id.
 * Handles the following actions:
 * - Delete success
 * - Delete success - Remove child from parents
 * - Create success - Add child to parent
 * - Add/Remove/Set entities for ManyToMany relationships
 * - Timeline
 * - Pagination
 *
 * @function
 * @param  {object} state - Redux state
 * @param  {string} action - Redux action
 */
const byId =
  (entityName: EntityName, entityNameUppercase: string) =>
  (state: any, action: Action): any => {
    if (action.type && action.type.startsWith('PREPARE_')) {
      return state;
    }

    if (
      action.type === RESET_POSITION &&
      action.entityName === entityName &&
      action.entities
    ) {
      const newState = { ...state };
      action.entities.forEach((entity) => {
        newState[entity.id] = {
          ...newState[entity.id],
          position: (entity as any).position,
        };
      });
      return newState;
    }

    // Updates per entity
    const updatedState = updateEntityById(state, action, entityName);
    if (updatedState) {
      return updatedState;
    }

    // Update entity for SUCCESS_TRANSFER_OWNERSHIP
    if (
      action.type ===
      `SUCCESS_${transferOwnershipActionType.toUpperCase}`
    ) {
      return mergeByIds(
        state,
        normalizeEntities(action.payload, action.meta.normalize)
          .entities[entityName],
        entityName,
      );
    }

    if (
      action.type === 'SET_ENTITY_VALUE' &&
      entityName === action.payload?.entityName
    ) {
      const { entityId, field, value } = action.payload;
      return {
        ...state,
        [entityId]: {
          ...state[entityId],
          [field]: value,
        },
      };
    }

    if (action.type && action.type.includes('_RAW_OUTPUT')) {
      return state;
    }

    if (
      action.meta &&
      action.type === `SUCCESS_DELETE_${entityNameUppercase}`
    ) {
      return state;
    }

    if (
      action.type === 'SUCCESS_CREATE_ENTITY_USER' &&
      entityName === action.meta.parentName
    ) {
      return previewArrayUpdate(state, action);
    }

    if (
      action.type === 'SUCCESS_ADD_TAG_TO_EXPERIMENT_WORKFLOW' &&
      entityName === 'experiment_workflow'
    ) {
      return previewFieldAdd(state, action, 'tags_preview');
    }

    if (
      action.type === 'SUCCESS_REMOVE_TAG_FROM_EXPERIMENT_WORKFLOW' &&
      entityName === 'experiment_workflow'
    ) {
      return previewFieldRemove(state, action, 'tags_preview');
    }

    if (
      action.meta &&
      action.meta.parentName &&
      action.meta.parentName === entityName &&
      (action.type.includes('SUCCESS_CREATE') ||
        action.type === 'SUCCESS_CONVERT_URL')
    ) {
      return addChildEntityToParent(state, action, entityName);
    }

    if (
      action.meta &&
      action.meta.entityName !== entityName &&
      action.type.includes('SUCCESS_CREATE_SIGNATURE_REQUIREMENT') &&
      entityName === 'entity_user_role_requirement'
    ) {
      return addChildEntityToParentBasedOnBodyEntityUserRoleRequirement(
        state,
        action,
      );
    }

    if (
      action.meta &&
      action.meta.entityName &&
      action.type.includes('SUCCESS_DELETE') &&
      parentOf(action.meta.entityName).includes(camelCase(entityName))
    ) {
      return removeChildEntityFromParents(state, action, entityName);
    }

    if (
      action.type &&
      action.type.startsWith('SUCCESS_ADD') &&
      action.meta &&
      action.meta.parentName === entityName
    ) {
      const newState = addManyToMany(state, action, entityName);
      if (
        action.payload?.entities &&
        action.payload?.entities[entityName]
      ) {
        return mergeByIds(
          newState,
          action.payload?.entities[entityName],
          entityName,
        );
      }
      return newState;
    }

    if (
      action.type &&
      action.type.startsWith('SUCCESS_REMOVE') &&
      action.meta &&
      action.meta.parentName === entityName
    ) {
      const newState = removeManyToMany(state, action, entityName);
      if (
        action.payload?.entities &&
        action.payload?.entities[entityName]
      ) {
        return mergeByIds(
          newState,
          action.payload?.entities[entityName],
          entityName,
        );
      }
      return newState;
    }

    if (
      action.type &&
      action.type.startsWith('SUCCESS_SET') &&
      action.meta &&
      action.meta.parentName === entityName
    ) {
      return setManyToMany(state, action, entityName);
    }

    // Optimistic update - Request
    if (
      action.meta &&
      action.meta.optimistic &&
      action.type &&
      action.type.startsWith('REQUEST') &&
      !action.type.includes('REQUEST_RAW') &&
      !action.meta.optimisticCreateGuid &&
      entityName === action.meta.entityName
    ) {
      let body = { ...action.meta.body };
      if (body) {
        body = Object.keys(body).reduce((result, key: string) => {
          const optimisticKey = getOptimisticKey(key);

          return { ...result, [optimisticKey]: body[key] };
        }, {});
      }

      return {
        ...state,
        [action.meta.identifier]: {
          ...state[action.meta.identifier],
          ...body,
        },
      };
    }

    // Optimistic update - success
    if (
      action.meta &&
      action.meta.optimistic &&
      action.type &&
      action.type.includes('SUCCESS') &&
      !action.type.includes('SUCCESS_RAW') &&
      entityName === action.meta.entityName
    ) {
      return state;
    }

    // Optimistic update - Fail
    if (
      action.meta &&
      action.meta.optimistic &&
      action.type &&
      action.type.includes('FALLBACK_UPDATE') &&
      entityName === action.meta.entityName &&
      action.fallbackBody
    ) {
      if (
        Object.values(action.fallbackBody).some(
          (value) => value === undefined,
        )
      ) {
        return state;
      }
      return {
        ...state,
        [action.meta.identifier]: {
          ...state[action.meta.identifier],
          ...action.fallbackBody,
        },
      };
    }

    // Has Payload
    if (action.payload) {
      if (
        action.type &&
        action.type.includes('SUCCESS') &&
        (action.type.includes('_PAGE_') ||
          action.type.includes('_CURSOR_'))
      ) {
        return mergeByIds(
          state,
          action.payload?.items.entities[entityName],
          entityName,
        );
      }
      if (
        action.payload?.entities &&
        action.payload?.entities[entityName]
      ) {
        return mergeByIds(
          state,
          action.payload?.entities[entityName],
          entityName,
        );
      }
    }

    return state;
  };

export default byId;
